import { lockScroll, unlockScroll } from '@theme/utilities';
import { draft, recent, cleanPath, accepted } from '@theme/gw-enquiry-core';

import { EnquiryForm } from '@theme/gw-enquiry-form';

// The panel form posts asynchronously after Shopify's supported protection.
class GWEnquiryWidget extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.panel = this.querySelector('dialog');
    this.launcher = this.querySelector('[data-enquiry-launcher]');
    this.formArea = this.querySelector('[data-enquiry-form-area]');
    if (!this.panel?.showModal || !this.formArea) return;
    this.storagePrefix = `gw-enquiry-v1-${this.dataset.themeId}-`;
    this.savedDraft = draft(this.read('draft'));
    if (!this.savedDraft) this.remove('draft');
    try { this.product = JSON.parse(this.querySelector('[data-enquiry-product-data]').textContent); } catch { this.product = null; }
    this.addEventListener('click', this.click, options);
    this.panel.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
    this.panel.addEventListener('close', () => { unlockScroll(this.panel); this.launcher.setAttribute('aria-expanded', 'false'); }, options);

    window.addEventListener('pagehide', () => { this.save(); this.close(false); }, options);
    document.addEventListener('dialog:open', this.otherOverlay, { ...options, capture: true });
    document.addEventListener('theme-drawer:open', this.otherOverlay, options);
    document.addEventListener('visitorConsentCollected', this.privacy, options);
    document.addEventListener('shopify:section:select', (event) => { if (event.target.contains(this)) this.open(this.launcher); }, options);
    document.addEventListener('shopify:section:deselect', (event) => { if (event.target.contains(this)) this.close(false); }, options);
    document.addEventListener('click', this.productClick, options);
    document.addEventListener('variant:update', () => this.sendContext(), options);
    document.addEventListener('change', () => this.sendContext(), options);
    this.privacy();
    if (window.Shopify?.loadFeatures && this.dataset.recent === 'true') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => this.privacy());
    }
    if (this.dataset.productTrigger === 'true') document.querySelectorAll('[data-gw-enquiry-product]').forEach(a => { a.hidden = false; });
    this.syncViewport();
    window.visualViewport?.addEventListener('resize', this.syncViewport, options);
    window.visualViewport?.addEventListener('scroll', this.syncViewport, options);
    this.panel.addEventListener('focusin', this.syncViewport, options);
    window.addEventListener('resize', this.syncViewport, options);
    // Observe overlay/control visibility instead of polling or fetching.
    this.overlayObserver = new MutationObserver(() => { this.syncClearance(); this.scheduleInvitation(); });
    this.overlayObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open', 'hidden', 'aria-expanded', 'class'] });
    this.syncClearance();
    document.addEventListener('visibilitychange', () => this.scheduleInvitation(), options);
    this.scheduleInvitation();
    this.dataset.ready = 'true';
    const form = this.querySelector('#GWEnquiryForm');
    if (this.transport?.form === form) this.transport.reconnect();
    else this.transport = new EnquiryForm(form, data => this.formEvent(data));
    this.formReady = true; this.querySelector('[data-enquiry-loading]').hidden = true;
    this.sendContext(); this.send({ type: 'gw-enquiry:restore', draft: draft(this.savedDraft), pending: false }); this.scheduleExpiry();
  }
  disconnectedCallback() {
    this.save(); this.close(false); this.abort?.abort(); this.abort = null;
    this.transport?.destroy();
    cancelAnimationFrame(this.viewportFrame); this.viewportFrame = null;
    this.overlayObserver?.disconnect(); clearTimeout(this.invitationTimer); clearTimeout(this.timeout); clearTimeout(this.expiryTimer);
    document.querySelectorAll('[data-gw-enquiry-product]').forEach(a => { a.hidden = true; });
  }
  read(key) { try { return JSON.parse(sessionStorage.getItem(this.storagePrefix + key)); } catch { return null; } }
  write(key, value) { try { sessionStorage.setItem(this.storagePrefix + key, JSON.stringify(value)); return true; } catch { return false; } }
  remove(key) { try { sessionStorage.removeItem(this.storagePrefix + key); } catch {} }
  save() { if (draft(this.savedDraft)) this.write('draft', this.savedDraft); else this.remove('draft'); }
  scheduleExpiry() {
    clearTimeout(this.expiryTimer);
    if (!this.savedDraft) return;
    this.expiryTimer = setTimeout(() => {
      this.savedDraft = null; this.remove('draft');
      if (!this.pending) this.send({type:'gw-enquiry:clear'});
    }, Math.max(1, 30 * 60 * 1000 - (Date.now() - this.savedDraft.updated)));
  }
  privacy = () => {
    let allowed = false;
    try { allowed = this.dataset.recent === 'true' && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; } catch {}
    const stored = this.read('recent');
    this.pages = recent(stored, location.origin, allowed);
    if (!allowed) this.remove('recent');
    else {
      const path = cleanPath(location.href, location.origin, true);
      if (path) this.pages = [{ path, title: this.querySelector('[data-enquiry-page-title]').textContent.slice(0, 160) }, ...this.pages.filter(p => p.path !== path)].slice(0, 5);
      if (this.pages.length) this.write('recent', { updated: Date.now(), pages: this.pages });
    }
    this.sendContext();
  };
  currentContext() {
    const path = cleanPath(location.href, location.origin) || '/';
    let product = null;
    if (this.product && cleanPath(this.product.path, location.origin, true)) {
      const selectedId = document.querySelector('[data-testid="product-information-details"] product-form-component input[name="id"]')?.value;
      const variant = this.product.variants?.find(v => String(v.id) === selectedId);
      product = { id: this.product.id, title: String(this.product.title).slice(0, 200), path: this.product.path, variant: variant ? { id: variant.id, title: String(variant.title).slice(0, 160) } : null };
    }
    let allowed = false;
    try { allowed = this.dataset.recent === 'true' && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; } catch {}
    if (!allowed) { this.pages = []; this.remove('recent'); }
    // A long-open page cannot send expired context even without another consent event.
    const pages = recent(this.read('recent'), location.origin, allowed).filter(p => p.path !== product?.path);
    return { origin: path, product, pages, editor: this.dataset.editor === 'true', blue: this.style.getPropertyValue('--gw-enquiry-blue') };
  }
  sendContext() { this.send({ type: 'gw-enquiry:context', ...this.currentContext() }); }
  send(value) { this.transport?.receive(value); }
  resetForm() {
    this.formArea.hidden = false;
    this.send({ type: 'gw-enquiry:clear' }); this.sendContext();
  }
  async open(opener = this.launcher) {
    if (this.panel.open || this.opening) return;
    this.opening = true; const request = (this.openRequest || 0) + 1; this.openRequest = request;
    this.dismissInvitation();
    document.querySelectorAll('gw-search').forEach(owner => owner.close?.());
    await Promise.allSettled([...document.querySelectorAll('dialog[open]')].filter(d => d !== this.panel).map(async d => {
      const owner = d.closest('dialog-component, theme-drawer, quick-add-dialog, gw-category-navigation');
      return owner?.closeDialog?.() ?? owner?.close?.(false);
    }));
    this.opening = false;
    if (request !== this.openRequest || !this.isConnected) return;
    this.returnFocus = opener;
    this.panel.showModal(); lockScroll(this.panel);
    this.syncViewport();
    this.launcher.setAttribute('aria-expanded', 'true');
    this.querySelector('[data-enquiry-close]').focus({ preventScroll: true });
    if (this.savedDraft && !draft(this.savedDraft)) { this.savedDraft = null; this.remove('draft'); this.send({type:'gw-enquiry:clear'}); }
    this.transport?.prepare();
    this.sendContext();
    this.dispatchEvent(new CustomEvent('dialog:open'));
  }
  close(restore = true) {
    this.openRequest = (this.openRequest || 0) + 1;
    if (!this.panel?.open) return;
    const active = !this.contains(document.activeElement) ? document.activeElement : null;
    this.panel.close(); unlockScroll(this.panel);
    if (restore) (this.returnFocus?.isConnected ? this.returnFocus : this.launcher).focus({ preventScroll: true });
    else active?.focus?.({ preventScroll: true });
  }
  otherOverlay = (event) => { if (!this.contains(event.target)) this.close(false); };
  productClick = (event) => {
    const opener = event.target.closest('[data-gw-enquiry-product]');
    if (!opener || this.dataset.productTrigger !== 'true') return;
    event.preventDefault(); this.open(opener);
  };
  click = (event) => {
    const target = event.target.closest('a,button');
    if (!target) return;
    if (target.matches('[data-enquiry-launcher],[data-enquiry-invite-open]')) { event.preventDefault(); this.open(this.launcher); }
    if (target.matches('[data-enquiry-close]')) this.close();
    if (target.matches('[data-enquiry-dismiss]')) this.dismissInvitation();
    if (target.matches('[data-enquiry-another]')) {
      this.pending = null; this.savedDraft = null; this.remove('draft');
      this.querySelector('[data-enquiry-receipt]').hidden = true;
      this.querySelector('[data-enquiry-intro]').hidden = false;
      this.shellError(''); this.resetForm();
    }
  };
  formEvent = (data) => {
    if (!data || typeof data !== 'object') return;
    if (data.type === 'gw-enquiry:refresh-context') this.sendContext();
    if (data.type === 'gw-enquiry:ready') {
      this.formReady = true;
      this.querySelector('[data-enquiry-loading]').hidden = true;
      if (this.querySelector('[data-enquiry-receipt]').hidden) this.formArea.hidden = false;
      this.sendContext();
      this.send({ type: 'gw-enquiry:restore', draft: draft(this.savedDraft), pending: !!this.pending });
    }
    if (data.type === 'gw-enquiry:draft') { this.savedDraft = draft(data.draft); this.save(); this.scheduleExpiry(); }
    if (data.type === 'gw-enquiry:discard') { this.savedDraft = null; this.remove('draft'); this.pending = null; this.shellError(''); }

    if (data.type === 'gw-enquiry:escape') this.close();
    if (data.type === 'gw-enquiry:submit' && /^[a-f0-9-]{36}$/.test(data.attempt)) {
      if (this.pending) return;
      this.pending = { attempt: data.attempt, preference: data.preference === 'Email' ? 'Email' : 'SMS', started: Date.now() };
      this.shellError(''); this.sendContext();
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => { if (this.pending) this.shellError('We cannot yet confirm receipt. A security check may be waiting below, or the request may have reached us. Your draft is retained. Please complete the check, or contact the store before sending again.'); }, 45000);
    }
    if (data.type === 'gw-enquiry:failure' && data.attempt === this.pending?.attempt) { clearTimeout(this.timeout); this.pending = null; }
    if (data.type === 'gw-enquiry:state') {
      if (accepted(data, this.pending, location.origin)) {
        clearTimeout(this.timeout); this.shellError('');
        const preference = this.pending.preference;
        this.pending = null; this.savedDraft = null; this.remove('draft');
        this.formArea.hidden = true;
        this.querySelector('[data-enquiry-intro]').hidden = true;
        this.querySelector('[data-enquiry-receipt]').hidden = false;
        this.querySelector('[data-enquiry-reply]').textContent = `Our team will get back to you by ${preference === 'Email' ? 'email' : 'text'} during opening hours.`;
        if (this.panel.open) this.querySelector('[data-enquiry-receipt-title]').focus({ preventScroll: true });
        this.send({ type: 'gw-enquiry:clear' });
      } else if (this.pending && data.errors === true) {
        clearTimeout(this.timeout); this.pending = null;
        this.shellError('Shopify could not accept this enquiry. Please check the form below. Your draft is retained.');
        this.send({ type: 'gw-enquiry:restore', draft: draft(this.savedDraft), pending: false });
      }
    }
  };
  shellError(text) { const error = this.querySelector('[data-enquiry-shell-error]'); error.textContent = text; error.hidden = !text; }
  syncViewport = () => {
    if (this.viewportFrame != null) return;
    this.viewportFrame = requestAnimationFrame(() => {
      this.viewportFrame = null;
      const viewport = window.visualViewport;
      this.style.setProperty('--gw-enquiry-vh', `${viewport?.height || innerHeight}px`);
      this.style.setProperty('--gw-enquiry-viewport-top', `${Math.max(0, viewport?.offsetTop || 0)}px`);
      this.syncClearance();
      this.revealFocusedField();
    });
  };
  revealFocusedField() {
    if (!this.panel?.open || !window.matchMedia('(max-width: 600px), (hover: none) and (pointer: coarse)').matches) return;
    const field = document.activeElement;
    if (!this.panel.contains(field) || !field?.matches('input:not([type="hidden"]), textarea, select')) return;
    const scroller = this.querySelector('.gw-enquiry__body');
    if (!scroller) return;
    const bounds = scroller.getBoundingClientRect();
    const rect = field.getBoundingClientRect();
    const top = bounds.top + 12;
    const bottom = bounds.bottom - 12;
    // Scroll only the panel's single mobile content area, never the underlying page.
    if (rect.top < top) scroller.scrollTop += rect.top - top;
    else if (rect.bottom > bottom) scroller.scrollTop += Math.min(rect.bottom - bottom, rect.top - top);
  }

  syncClearance() {
    let clearance = 0;
    const maximum = Math.min(160, innerHeight * 0.25);
    // Only real, small bottom controls can move the launcher. Preview/CAPTCHA
    // iframes can cover the whole viewport even when their UI is transparent.
    for (const el of document.querySelectorAll('.sticky-add-to-cart__bar[data-stuck="true"], #shopify-pc__banner, [data-enquiry-avoid]')) {
      const rect = el.getBoundingClientRect();
      const style = getComputedStyle(el);
      if (el.hidden || style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) continue;
      if (style.position !== 'fixed' && style.position !== 'sticky') continue;
      const lift = innerHeight - rect.top + 8;
      if (rect.width > 0 && rect.height > 0 && rect.height <= maximum && rect.top >= 0 && rect.top < innerHeight && rect.bottom >= innerHeight - 48 && lift <= maximum) clearance = Math.max(clearance, lift);
    }
    const value = `${clearance}px`;
    if (this.style.getPropertyValue('--gw-enquiry-clearance') !== value) this.style.setProperty('--gw-enquiry-clearance', value);
  }
  busyOverlay() { return !!document.querySelector('dialog[open],gw-search input[aria-expanded="true"],#shopify-pc__banner') || document.hidden; }
  dismissInvitation() { clearTimeout(this.invitationTimer); this.querySelector('[data-enquiry-invitation]').hidden = true; this.invitationDone = true; this.write('invited', true); }
  scheduleInvitation() {
    if (this.invitationDone || this.dataset.invitation !== 'true' || this.dataset.editor === 'true') return;
    if (this.read('invited')) { this.invitationDone = true; return; }
    if (!this.write('storage-check', true)) return;
    this.remove('storage-check');
    if (this.busyOverlay()) {
      if (this.invitationTimer) {
        this.invitationRemaining = Math.max(0, this.invitationRemaining - (Date.now() - this.invitationStarted));
        clearTimeout(this.invitationTimer); this.invitationTimer = null;
      }
      const invitation = this.querySelector('[data-enquiry-invitation]');
      if (!invitation.hidden) invitation.hidden = true;
      return;
    }
    if (this.invitationTimer) return;
    this.invitationRemaining ??= Number(this.dataset.delay || 12) * 1000;
    this.invitationStarted = Date.now();
    this.invitationTimer = setTimeout(() => {
      this.invitationTimer = null;
      if (this.busyOverlay() || this.invitationDone) return;
      this.querySelector('[data-enquiry-invitation]').hidden = false;
      this.invitationDone = true; this.write('invited', true);
    }, this.invitationRemaining);

  }
}
if (!customElements.get('gw-enquiry-widget')) customElements.define('gw-enquiry-widget', GWEnquiryWidget);
